import { useEffect, useMemo } from 'react'

import Box from '@ilg-totallymoney/ui/components/Box'
import Grid from '@ilg-totallymoney/ui/components/Grid'
import LinkButton from '@ilg-totallymoney/ui/components/LinkButton'
import {
    AlternativeProduct,
    LoanProductCategory,
} from '@mediaingenuity/totallymoney.types'
import { captureException } from '@sentry/nextjs'

import {
    GetUnscoredCategoriesFilter,
    UnscoredLoanProduct,
    UnscoredLoanProductsCategory,
} from '@appTypes/LoansApi.interface'

import ErrorBoundary from '@components/ErrorBoundary'
import LoanTypeModal from '@components/LoanTypeModal'
import RepresentativeExampleModal from '@components/RepresentativeExampleModal'

import useAppQueryState from '@hooks/useAppQueryState'
import useLogEvents from '@hooks/useLogEvents'
import useSetState from '@hooks/useSetState'

import { getAppConfig } from '@utils/getAppConfig'
import getCategoryByCategoryId from '@utils/getCategoryByCategoryId'
import { getRedirectAccountUrl } from '@utils/getRedirectAccountUrl'
import { getSortComparator } from '@utils/getSortComparator'

import { SkeletonUnscoredTableItem } from '../UnscoredTableSkeleton/UnscoredTableSkeleton'
import NoHomeownerLoansBanner from './components/NoHomeownerLoansBanner'
import NoResultsBanner from './components/NoResultsBanner'
import TableHeader from './components/UnscoredTableHeader'
import UnscoredTableItem from './components/UnscoredTableItem'

interface UnscoredTableProps {
    activeCategoryId: LoanProductCategory
    alternativeProducts: AlternativeProduct[]
    filter: GetUnscoredCategoriesFilter
    isUpdatingResults: boolean
    unscoredCategories: UnscoredLoanProductsCategory[]
}

interface UnscoredTableState {
    selectedCategory: LoanProductCategory
    selectedSortKey: any
}

enum ModalType {
    LoanType = 'LoanType',
    RepresentativeExample = 'RepresentativeExample',
}

interface ModalState {
    activeLoan: UnscoredLoanProduct | null
    activeModal: ModalType | null
}

export const defaultModalState: ModalState = {
    activeLoan: null,
    activeModal: null,
}

const UnscoredTable = ({
    unscoredCategories,
    filter,
    activeCategoryId,
    isUpdatingResults,
}: UnscoredTableProps) => {
    const appConfig = getAppConfig()
    const { appQueryState } = useAppQueryState()
    const { isReady } = useAppQueryState()

    const logEvents = useLogEvents()

    const [loansState, setLoansState] = useSetState<UnscoredTableState>({
        selectedCategory: appQueryState.categoryId!,
        selectedSortKey: '',
    })

    const [modalState, updateModalState] =
        useSetState<ModalState>(defaultModalState)

    const showAlternativeProducts = activeCategoryId === 'AlternativeProducts'

    const activeCategory = getCategoryByCategoryId(
        unscoredCategories!,
        loansState.selectedCategory
    )

    const activeCategoryLoans = useMemo(() => {
        if (activeCategory == null) {
            return null
        }

        return activeCategory.loans.sort(getSortComparator('recommenderScore'))
    }, [activeCategory, loansState.selectedCategory])

    useEffect(() => {
        setLoansState({ selectedCategory: activeCategoryId })
    }, [activeCategoryId])

    const resetModalState = () => {
        updateModalState(defaultModalState)
    }

    const getLoanRedirectAccountUrl = (loan: UnscoredLoanProduct) => {
        const data = {
            queryObject: {
                category: appQueryState.category,
                loanAmount: Number(filter.loanAmount),
                loanTerm: Number(filter.loanTerm),
                client: appConfig.NAME_WHITELABEL,
                loanId: loan.loanId,
            },
        }

        return getRedirectAccountUrl(data)
    }

    const onCheckEligibilityButtonPress = ({
        loan,
    }: {
        loan: UnscoredLoanProduct
    }) => {
        logEvents.logCheckEligibilityEvent({
            category: appQueryState.categoryId!,
            loanAmount: Number(filter.loanAmount),
            loanTerm: Number(filter.loanTerm),
            origin: loan.loanId,
        })

        window.location.href = getLoanRedirectAccountUrl(loan)
    }

    const showNoHomeownerBanner =
        activeCategoryId === 'Secured' && activeCategoryLoans?.length === 0

    const showNoResultsBanner =
        !isUpdatingResults &&
        !showNoHomeownerBanner &&
        (activeCategoryLoans?.length === 0 || showAlternativeProducts)

    return (
        <Box py="space20">
            <Box mb="space30">
                <TableHeader
                    categories={unscoredCategories}
                    filter={filter}
                    activeCategory={activeCategoryId}
                    sortValue={loansState.selectedSortKey}
                    onCategoryChange={() => void 0}
                    onSortChange={() => void 0}
                />
            </Box>

            <Box mb="space50">
                <Grid>
                    <Box
                        as="ul"
                        gridColumn="1/13"
                        display="grid"
                        gridGap="space30"
                        mb="space30"
                    >
                        {isUpdatingResults ? (
                            <Box data-testid="skeleton-loading">
                                <SkeletonUnscoredTableItem />
                                <SkeletonUnscoredTableItem />
                                <SkeletonUnscoredTableItem />
                            </Box>
                        ) : null}

                        {!isUpdatingResults ? (
                            <>
                                {showNoHomeownerBanner ? (
                                    <Box
                                        gridColumn="1/13"
                                        pt={['space10', 'space30']}
                                    >
                                        <NoHomeownerLoansBanner />
                                    </Box>
                                ) : null}

                                {showNoResultsBanner ? (
                                    <Box p={['space10', 'space30']}>
                                        <NoResultsBanner
                                            category={activeCategoryId}
                                        />
                                    </Box>
                                ) : null}

                                {!showAlternativeProducts
                                    ? activeCategoryLoans?.map(loan => (
                                          <ErrorBoundary
                                              key={loan.propositionId}
                                              fallback={null}
                                              onError={({ error }) => {
                                                  console.log(error)
                                                  captureException(error)
                                              }}
                                          >
                                              <Box as="li">
                                                  <UnscoredTableItem
                                                      item={loan}
                                                      filter={filter}
                                                      onShowRepresentativeExampleModal={() =>
                                                          updateModalState({
                                                              activeLoan: loan,
                                                              activeModal:
                                                                  ModalType.RepresentativeExample,
                                                          })
                                                      }
                                                      onShowLoanTypeModal={() =>
                                                          updateModalState({
                                                              activeLoan: loan,
                                                              activeModal:
                                                                  ModalType.LoanType,
                                                          })
                                                      }
                                                      renderButtons={() => (
                                                          <>
                                                              <LinkButton
                                                                  href={getLoanRedirectAccountUrl(
                                                                      loan
                                                                  )}
                                                                  disabled={
                                                                      !isReady
                                                                  }
                                                                  variant="primary"
                                                                  text="Check my eligibility"
                                                                  onClick={event => {
                                                                      event.preventDefault()

                                                                      onCheckEligibilityButtonPress(
                                                                          {
                                                                              loan,
                                                                          }
                                                                      )
                                                                  }}
                                                                  data-gtm-event="loans-postClick-personal"
                                                              />
                                                          </>
                                                      )}
                                                  />
                                              </Box>
                                          </ErrorBoundary>
                                      ))
                                    : []}
                            </>
                        ) : null}
                    </Box>
                </Grid>
            </Box>

            <RepresentativeExampleModal
                loanType={modalState.activeLoan?.type}
                showModal={
                    modalState.activeModal == ModalType.RepresentativeExample
                }
                onModalClose={resetModalState}
            />
            <LoanTypeModal
                loanName={modalState.activeLoan?.loanName}
                showModal={modalState.activeModal == ModalType.LoanType}
                onModalClose={resetModalState}
            />
        </Box>
    )
}

export default UnscoredTable
